// Pure TS Layer: React 非依存
// MQTT モニタの受信メッセージリングバッファ + 接続状態ストア

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ConnectionState, IncomingMessage } from '@/hooks/mqtt/mqttClient';

export const MAX_BUFFER_SIZE = 500;

export type MonitorScope = 'state_transition' | 'all';

export type StoredMessage = {
  readonly id: string;
  readonly topic: string;
  /** payload を JSON parse できた場合は object、できない場合は string */
  readonly payload: unknown;
  readonly rawPayload: string;
  readonly receivedAt: Date;
};

type State = {
  connection: ConnectionState;
  connectionDetail: string | null;
  /** 設定エラー (環境変数欠落など) */
  configError: string | null;
  /** リングバッファ: 古いものから drop */
  messages: StoredMessage[];
  paused: boolean;
  scope: MonitorScope;
  /** 受信フィルタ (topic substring) */
  topicFilter: string;
  /** 累計受信数 (デバッグ用、UI 表示) */
  totalReceived: number;
  /** リングバッファで drop された累計 */
  totalDropped: number;
};

type Actions = {
  setConnection: (state: ConnectionState, detail?: string | null) => void;
  setConfigError: (msg: string | null) => void;
  appendMessage: (msg: IncomingMessage) => void;
  clearMessages: () => void;
  setPaused: (paused: boolean) => void;
  setScope: (scope: MonitorScope) => void;
  setTopicFilter: (filter: string) => void;
  reset: () => void;
};

const initialState: State = {
  connection: 'idle',
  connectionDetail: null,
  configError: null,
  messages: [],
  paused: false,
  scope: 'all',
  topicFilter: '',
  totalReceived: 0,
  totalDropped: 0,
};

function tryParsePayload(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function makeId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export const useMqttMonitorStore = create<State & Actions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setConnection: (state, detail) =>
        set({ connection: state, connectionDetail: detail ?? null }),

      setConfigError: (msg) => set({ configError: msg }),

      appendMessage: (msg) => {
        const { paused, messages, totalReceived, totalDropped } = get();
        const stored: StoredMessage = {
          id: makeId(),
          topic: msg.topic,
          payload: tryParsePayload(msg.payload),
          rawPayload: msg.payload,
          receivedAt: msg.receivedAt,
        };
        // paused でも内部カウントは進める (バッファ表示のみ凍結)
        if (paused) {
          set({ totalReceived: totalReceived + 1 });
          return;
        }
        const next = [stored, ...messages];
        let dropped = totalDropped;
        if (next.length > MAX_BUFFER_SIZE) {
          dropped += next.length - MAX_BUFFER_SIZE;
          next.length = MAX_BUFFER_SIZE;
        }
        set({
          messages: next,
          totalReceived: totalReceived + 1,
          totalDropped: dropped,
        });
      },

      clearMessages: () => set({ messages: [], totalReceived: 0, totalDropped: 0 }),
      setPaused: (paused) => set({ paused }),
      setScope: (scope) => set({ scope }),
      setTopicFilter: (filter) => set({ topicFilter: filter }),
      reset: () => set(initialState),
    }),
    { name: 'mqtt-monitor-store' },
  ),
);
